import { Box, Chip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { getCleanSentenceText } from "../utils/englishPaperDetection";

/**
 * Renders an English error-finding MCQ in GL Assessment 11+ paper style.
 *
 * PDF layout matched:
 *
 *   The local   superior players   dominated the   tennis tornament.
 *   county's
 *   └─────────────── A ──────────┘└──── B ────────┘└─── C ──┘└─ D ─┘
 *                    A                  B               C        D
 *
 *   [ A ]  [ B ]  [ C ]  [ D ]  [ n No mistake ]
 *
 * Each column renders: word-group text → U-bracket → letter label.
 * Column widths are proportional to character spans so text and brackets align exactly.
 * Falls back to equal widths when option texts cannot be located in the sentence.
 */
const EnglishErrorFindingLayout = ({
  question,
  selected,
  onSelect,
  showAnswers = false,
  studentAnswer,
  mode = "preview",
}) => {
  if (!question) return null;

  const options = question.options || [];

  // Separate N "no mistake" option from labelled word-group options
  const nOption = options.find((o) =>
    /^N$/i.test(((o.option_text || o.text || "")).trim())
  );
  const letterOptions = options
    .filter((o) => !/^N$/i.test(((o.option_text || o.text || "")).trim()))
    .map((opt, idx) => {
      const displayText = opt.option_text || opt.text || "";
      return {
        ...opt,
        letter: String.fromCharCode(65 + idx),
        displayText,
        value: mode === "take" && opt.id ? opt.id : displayText,
      };
    });

  const nValue = nOption
    ? mode === "take" && nOption.id
      ? nOption.id
      : nOption.option_text || nOption.text || "N"
    : null;

  // Clean sentence: strip ↑ markers and trailing "A B C D" labels
  const cleanSentence = getCleanSentenceText(question.question_text || "");

  // Effective answer for highlighting
  const effectiveAnswer = mode === "review" ? (studentAnswer ?? selected) : selected;

  // Correct option values
  const correctValues = (() => {
    const correctOpts = options.filter((o) => o.is_correct);
    if (correctOpts.length > 0) return correctOpts.map((o) => o.option_text || o.text || "");
    const correctIndices = question.answer_data?.correct_options || [];
    return correctIndices.map((i) => options[i] ? (options[i].option_text || options[i].text || "") : "");
  })();

  const correctIds = options.filter((o) => o.is_correct).map((o) => o.id).filter((id) => id != null);

  const isCorrectValue = (val, displayText) => {
    if (correctIds.length > 0 && val != null && correctIds.includes(val)) return true;
    const text = displayText ?? val;
    return correctValues.includes(text);
  };

  // ── Compute proportional bracket widths ─────────────────────────────────────
  // Try to locate each option's text sequentially in the sentence.
  // If all are found, use their character-span lengths for proportional widths.
  // Otherwise fall back to equal widths (handles old imports with incorrect option data).
  const computeSegmentWidths = () => {
    if (!cleanSentence || letterOptions.length === 0) return null;

    const sentenceLower = cleanSentence.toLowerCase();
    const spans = [];
    let cursor = 0;

    for (const opt of letterOptions) {
      const text = (opt.displayText || "").trim();
      // Skip if text is too short or is just a letter label (bad option data)
      if (text.length < 2 || /^[A-E]$/.test(text)) return null;

      const idx = sentenceLower.indexOf(text.toLowerCase(), cursor);
      if (idx === -1) return null; // can't find it → fall back to equal

      spans.push(text.length);
      cursor = idx + text.length;
    }

    if (spans.length !== letterOptions.length) return null;

    const total = spans.reduce((s, l) => s + l, 0);
    if (total === 0) return null;

    return spans.map((len) => (len / total) * 100);
  };

  const segmentWidths = computeSegmentWidths();
  // Use computed widths if available, otherwise equal widths
  const widths = segmentWidths || letterOptions.map(() => 100 / (letterOptions.length || 4));
  const segCount = letterOptions.length || 4;

  // ── Button style helper ──────────────────────────────────────────────────────
  const getButtonSx = (value, displayText) => {
    const isSelected = effectiveAnswer === value;
    const isCorrect = isCorrectValue(value, displayText);
    const isWrong = showAnswers && isSelected && !isCorrect;
    const isRight = showAnswers && isCorrect;

    return {
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 52,
      minHeight: 52,
      px: 1.5,
      py: 0.75,
      borderRadius: 1,
      border: "2px solid",
      borderColor: isWrong ? "error.main" : isRight ? "success.main" : isSelected ? "primary.main" : "#90b4d8",
      bgcolor: isWrong ? "#fef2f2" : isRight ? "#f0fdf4" : isSelected ? "#eff6ff" : "white",
      cursor: onSelect ? "pointer" : "default",
      transition: "all 0.15s",
      userSelect: "none",
      flexShrink: 0,
      "&:hover": onSelect
        ? { borderColor: isWrong ? "error.main" : isRight ? "success.main" : "primary.main", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }
        : {},
    };
  };

  const letterColor = (value, displayText) => {
    const isSelected = effectiveAnswer === value;
    const isCorrect = isCorrectValue(value, displayText);
    if (showAnswers && isSelected && !isCorrect) return "error.main";
    if (showAnswers && isCorrect) return "success.main";
    if (isSelected) return "primary.main";
    return "#1565c0";
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* ── Sentence + Bracket bar combined ────────────────────────────────────
          Each column holds the word-group text at top (forming the sentence broken
          by segment), then the U-shaped bracket below, then the letter label.
          Widths are proportional to character spans so text and brackets align exactly.
       ── */}
      <Box sx={{ display: "flex", width: "100%", mb: 1.5, alignItems: "flex-end" }}>
        {Array.from({ length: segCount }).map((_, idx) => {
          const wordGroup = letterOptions[idx]?.displayText || "";
          return (
            <Box
              key={idx}
              sx={{
                width: `${widths[idx] ?? (100 / segCount)}%`,
                ml: idx === 0 ? 0 : "-1.5px", // merge adjacent side borders → no double lines
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* ── Word-group text — top of column, IS the sentence broken into segments ── */}
              <Typography
                sx={{
                  fontSize: "0.98rem",
                  color: "text.primary",
                  lineHeight: 1.6,
                  textAlign: "center",
                  wordBreak: "break-word",
                  px: 0.25,
                  width: "100%",
                  fontFamily: '"Georgia", "Times New Roman", serif',
                }}
              >
                {wordGroup}
              </Typography>
              {/* ── Bracket frame: U-shape open at top, sits right under the word text ── */}
              <Box
                sx={{
                  width: "100%",
                  height: 10,
                  borderLeft: "1.5px solid",
                  borderLeftColor: "text.secondary",
                  borderRight: "1.5px solid",
                  borderRightColor: "text.secondary",
                  borderBottom: "1.5px solid",
                  borderBottomColor: "text.secondary",
                  flexShrink: 0,
                  mt: 0.25,
                }}
              />
              {/* ── Letter label — appears BELOW the bracket line ── */}
              <Typography
                sx={{
                  color: "#1565c0",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  lineHeight: 1,
                  letterSpacing: "0.02em",
                  mt: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                {String.fromCharCode(65 + idx)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Answer buttons: A B C D … N ── */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {letterOptions.map(({ letter, value, displayText }) => (
          <Box key={letter} onClick={() => onSelect && onSelect(value)} sx={getButtonSx(value, displayText)}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1, color: letterColor(value, displayText) }}>
              {letter}
            </Typography>
            {showAnswers && effectiveAnswer === value && !isCorrectValue(value, displayText) && (
              <CancelIcon sx={{ fontSize: 13, color: "error.main", mt: 0.25 }} />
            )}
            {showAnswers && isCorrectValue(value, displayText) && (
              <CheckCircleIcon sx={{ fontSize: 13, color: "success.main", mt: 0.25 }} />
            )}
          </Box>
        ))}

        {/* N — No mistake */}
        {nValue && (
          <Box onClick={() => onSelect && onSelect(nValue)} sx={{ ...getButtonSx(nValue, nOption?.option_text || nOption?.text || "N"), minWidth: 80, px: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1, color: letterColor(nValue, nOption?.option_text || nOption?.text || "N") }}>
              n
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "text.secondary", lineHeight: 1.1, mt: 0.25 }}>
              No mistake
            </Typography>
            {showAnswers && effectiveAnswer === nValue && !isCorrectValue(nValue, nOption?.option_text || nOption?.text || "N") && (
              <CancelIcon sx={{ fontSize: 13, color: "error.main", mt: 0.25 }} />
            )}
            {showAnswers && isCorrectValue(nValue, nOption?.option_text || nOption?.text || "N") && (
              <CheckCircleIcon sx={{ fontSize: 13, color: "success.main", mt: 0.25 }} />
            )}
          </Box>
        )}
      </Box>

      {/* ── Review: correct answer chip when student got it wrong ── */}
      {showAnswers && effectiveAnswer && !isCorrectValue(effectiveAnswer) && correctValues.length > 0 && (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Correct answer:
          </Typography>
          <Chip
            label={correctValues[0] === "N" ? "N — No mistake" : correctValues[0]}
            size="small"
            sx={{ bgcolor: "#f0fdf4", color: "success.dark", fontWeight: 700, fontSize: "0.75rem" }}
          />
        </Box>
      )}
    </Box>
  );
};

export default EnglishErrorFindingLayout;
